import { Cross2Icon } from '@radix-ui/react-icons'
import { KestrelViewerContext } from 'features/kestrelViewer/KestrelViewerProvider'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { PhotoCrosshair } from 'features/kestrelViewer/ui/tools/photoreel/components/PhotoCrosshair'
import { usePhotoUrl } from 'features/kestrelViewer/ui/tools/photoreel/hooks/usePhotoUrl'
import { PhotoReelToggle } from 'features/kestrelViewer/ui/tools/photoreel/PhotoReelToggle'
import { PhotoViewer } from 'features/kestrelViewer/ui/tools/photoreel/PhotoViewer'
import React, { useContext, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { KVPhoto } from 'types'

// Smaller thumbnail size for docked panel
const THUMBNAIL_SIZE_PX = 96

// Toggle controls row
const ControlsRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
`

const PanelContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 1rem;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
`

const Title = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: white;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  z-index: 10;
  position: relative;

  &:hover {
    color: white;
    background: rgba(255, 255, 255, 0.1);
  }
`

const LoadingText = styled.div`
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  padding: 2rem;
`

// Main image preview area
const PreviewArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0.75rem 0;
  overflow: hidden;
  position: relative;
  background: transparent;
  border-radius: 8px;
  min-height: 200px;
`

const NoPhotoText = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.875rem;
`

// Thumbnail strip at bottom
const ThumbnailStrip = styled.div`
  flex-shrink: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 0.75rem;
`

const ThumbnailScrollContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding-bottom: 0.25rem;

  /* Hide scrollbar but keep functionality */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) transparent;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }
`

const ThumbnailItem = styled.div<{ $selected: boolean }>`
  width: ${THUMBNAIL_SIZE_PX}px;
  height: ${THUMBNAIL_SIZE_PX}px;
  min-width: ${THUMBNAIL_SIZE_PX}px;
  min-height: ${THUMBNAIL_SIZE_PX}px;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  background: ${({ theme }) => theme.forecast.background3};
  border: 2px solid ${({ $selected }) => ($selected ? 'white' : 'transparent')};
  transition:
    border-color 0.15s ease,
    transform 0.15s ease;

  &:hover {
    transform: scale(1.03);
  }
`

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const ThumbnailLoading = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.75rem;
`

// Small thumbnail component for the strip
const SmallThumbnail: React.FC<{
  photo: KVPhoto
  selected: boolean
  onClick: () => void
}> = ({ photo, selected, onClick }) => {
  const { url, isLoading } = usePhotoUrl(photo, 'thumbnail')
  const [imageDimensions, setImageDimensions] = useState<{
    width: number
    height: number
  } | null>(null)

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    setImageDimensions({
      width: img.naturalWidth,
      height: img.naturalHeight,
    })
  }

  const projection = photo.projection

  return (
    <ThumbnailItem $selected={selected} onClick={onClick}>
      {isLoading ? (
        <ThumbnailLoading>...</ThumbnailLoading>
      ) : url ? (
        <ThumbnailImage
          src={url}
          alt='Photo thumbnail'
          onLoad={handleImageLoad}
        />
      ) : (
        <ThumbnailLoading>No image</ThumbnailLoading>
      )}
      {photo.hasThumbnail && projection && imageDimensions && !isLoading && (
        <PhotoCrosshair
          x={projection.x}
          y={projection.y}
          scale={0.5}
          containerWidth={THUMBNAIL_SIZE_PX}
          containerHeight={THUMBNAIL_SIZE_PX}
          imageWidth={imageDimensions.width}
          imageHeight={imageDimensions.height}
          originalImageWidth={projection.originalImageWidth}
          originalImageHeight={projection.originalImageHeight}
        />
      )}
    </ThumbnailItem>
  )
}

export const DockedPhotoReelPanel: React.FC = () => {
  // Check if we're inside the KestrelViewerProvider before using the context
  const viewerContext = useContext(KestrelViewerContext)

  // Get photos and selection from context
  const photos = viewerContext?.photos ?? []
  const filteredPhotos = viewerContext?.filteredPhotos
  const selectedPhoto = viewerContext?.selectedPhoto
  const setSelectedPhoto = viewerContext?.setSelectedPhoto
  const filteringPhotos = viewerContext?.filteringPhotos ?? false
  const sendCommand = viewerContext?.sendCommand
  const settings = viewerContext?.settings
  const navigationMode = viewerContext?.navigationMode ?? 'orbit'

  // Camera poses visibility - shared with toolbar toggle
  const cameraPosesVisible = viewerContext?.cameraPosesVisible ?? false
  const setCameraPosesVisible = viewerContext?.setCameraPosesVisible

  // Rotation state and management
  const getPhotoRotation = viewerContext?.getPhotoRotation
  const setPhotoRotation = viewerContext?.setPhotoRotation

  // Get current rotation for selected photo
  const currentRotation = selectedPhoto
    ? (getPhotoRotation?.(selectedPhoto.id) ?? 0)
    : 0

  const handleRotationChange = (newRotation: number) => {
    if (selectedPhoto && setPhotoRotation) {
      setPhotoRotation(selectedPhoto.id, newRotation)
    }
  }

  // Local toggle states for projection lines and highlight
  const [poseProjectionsEnabled, setPoseProjectionsEnabled] = useState(false)
  const [highlightCurrentPoseEnabled, setHighlightCurrentPoseEnabled] =
    useState(false)

  const isLocked = navigationMode === 'locked'

  // Clear projection/highlight when entering locked mode
  useEffect(() => {
    if (isLocked) {
      setPoseProjectionsEnabled(false)
      setHighlightCurrentPoseEnabled(false)
      sendCommand?.({ command: KestrelCommands.HIDE_CAMERA_POSE_PROJECTIONS })
      sendCommand?.({
        command: KestrelCommands.HIGHLIGHT_CAMERA_POSE,
        highlight: false,
      })
    }
  }, [isLocked, sendCommand])

  const handleCameraPosesToggle = () => {
    const newValue = !cameraPosesVisible
    setCameraPosesVisible?.(newValue)
    sendCommand?.({
      command: KestrelCommands.SET_CAMERA_POSES_VISIBILITY,
      cameraPosesVisible: newValue,
    })
  }

  const handlePoseProjectionsToggle = () => {
    const newValue = !poseProjectionsEnabled
    setPoseProjectionsEnabled(newValue)
    if (newValue) {
      if (filteredPhotos && filteredPhotos.length > 0) {
        sendCommand?.({
          command: KestrelCommands.SHOW_CAMERA_POSE_PROJECTIONS,
          imageIds: filteredPhotos.map((photo) => photo.filename),
        })
      }
    } else {
      sendCommand?.({
        command: KestrelCommands.HIDE_CAMERA_POSE_PROJECTIONS,
      })
    }
  }

  useEffect(() => {
    if (poseProjectionsEnabled && filteredPhotos && filteredPhotos.length > 0) {
      sendCommand?.({
        command: KestrelCommands.SHOW_CAMERA_POSE_PROJECTIONS,
        imageIds: filteredPhotos.map((photo) => photo.filename),
      })
    }
  }, [filteredPhotos, poseProjectionsEnabled, sendCommand])

  const handleHighlightCurrentPoseToggle = () => {
    const newValue = !highlightCurrentPoseEnabled
    setHighlightCurrentPoseEnabled(newValue)
    sendCommand?.({
      command: KestrelCommands.HIGHLIGHT_CAMERA_POSE,
      imageId: selectedPhoto?.filename,
      highlight: newValue,
    })
  }

  // Update highlight when selected photo changes (if toggle is on)
  useEffect(() => {
    if (highlightCurrentPoseEnabled && selectedPhoto) {
      sendCommand?.({
        command: KestrelCommands.HIGHLIGHT_CAMERA_POSE,
        imageId: selectedPhoto.filename,
        highlight: true,
      })
    }
  }, [selectedPhoto, highlightCurrentPoseEnabled, sendCommand])

  // Send orbit display persistence command when panel opens/closes
  useEffect(() => {
    sendCommand?.({
      command: KestrelCommands.SET_ORBIT_DISPLAY_PERSISTENT,
      persistent: true,
    })

    return () => {
      const restorePersistent = settings?.orbitCenterPersistent ?? false
      sendCommand?.({
        command: KestrelCommands.SET_ORBIT_DISPLAY_PERSISTENT,
        persistent: restorePersistent,
      })
      sendCommand?.({
        command: KestrelCommands.HIDE_CAMERA_POSE_PROJECTIONS,
      })
      sendCommand?.({
        command: KestrelCommands.HIGHLIGHT_CAMERA_POSE,
        highlight: false,
      })
    }
  }, [sendCommand, settings?.orbitCenterPersistent])

  // Preview area dimensions for PhotoViewer
  const previewRef = useRef<HTMLDivElement>(null)
  const [previewDimensions, setPreviewDimensions] = useState({
    width: 400,
    height: 300,
  })

  // Measure preview area dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (previewRef.current) {
        const { clientWidth, clientHeight } = previewRef.current
        setPreviewDimensions({
          width: clientWidth,
          height: clientHeight,
        })
      }
    }

    updateDimensions()
    window.addEventListener('resize', updateDimensions)

    // Use ResizeObserver for more accurate sizing
    const resizeObserver = new ResizeObserver(updateDimensions)
    if (previewRef.current) {
      resizeObserver.observe(previewRef.current)
    }

    return () => {
      window.removeEventListener('resize', updateDimensions)
      resizeObserver.disconnect()
    }
  }, [])

  const displayPhotos = filteredPhotos ?? photos

  // Track the previous filtered photos to detect when it changes
  const prevFilteredPhotosRef = useRef<typeof filteredPhotos>(null)

  // Always select the first photo when filtered photos changes (orbit moved)
  // This ensures the "best graded" photo is always selected
  useEffect(() => {
    // Check if filteredPhotos changed (different reference means new filter results)
    const filteredPhotosChanged =
      filteredPhotos !== prevFilteredPhotosRef.current
    prevFilteredPhotosRef.current = filteredPhotos

    if (filteredPhotosChanged && displayPhotos.length > 0 && setSelectedPhoto) {
      // Select the first photo (best graded) when filter results change
      setSelectedPhoto(displayPhotos[0])
      return
    }

    // Initial load: select first photo if nothing selected
    if (displayPhotos.length > 0 && !selectedPhoto && setSelectedPhoto) {
      setSelectedPhoto(displayPhotos[0])
    }
  }, [filteredPhotos, displayPhotos, selectedPhoto, setSelectedPhoto])

  const handleThumbnailClick = (photo: KVPhoto) => {
    setSelectedPhoto?.(photo)
  }

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    // Clear selected photo to prevent modal from showing
    setSelectedPhoto?.(null)
    // Close all photo reel modes - exit completely
    viewerContext?.setPhotoReelDocked(false)
    viewerContext?.setPhotoReelOpen(false)
  }

  if (!viewerContext) {
    return (
      <PanelContainer>
        <LoadingText>Loading photo reel...</LoadingText>
      </PanelContainer>
    )
  }

  return (
    <PanelContainer>
      <Header>
        <Title>Photo Reel</Title>
        <CloseButton
          type='button'
          onClick={handleClose}
          title='Close photo reel'>
          <Cross2Icon width={20} height={20} />
        </CloseButton>
      </Header>

      {/* Toggle controls */}
      <ControlsRow>
        <PhotoReelToggle
          label='Show Camera Poses'
          active={cameraPosesVisible}
          onToggle={handleCameraPosesToggle}
        />
        <PhotoReelToggle
          label='Camera Pose Projections'
          active={poseProjectionsEnabled}
          onToggle={handlePoseProjectionsToggle}
          disabled={isLocked}
        />
        <PhotoReelToggle
          label='Highlight Current Pose'
          active={highlightCurrentPoseEnabled}
          onToggle={handleHighlightCurrentPoseToggle}
          disabled={isLocked}
        />
      </ControlsRow>

      {/* Main preview area with PhotoViewer */}
      <PreviewArea ref={previewRef}>
        {filteringPhotos ? (
          <NoPhotoText>Updating...</NoPhotoText>
        ) : selectedPhoto ? (
          <PhotoViewer
            photo={selectedPhoto}
            rotation={currentRotation}
            onRotationChange={handleRotationChange}
            fillContainer={true}
            containerWidth={previewDimensions.width}
            containerHeight={previewDimensions.height}
            compactToolbar={true}
            showCrosshair={true}
          />
        ) : (
          <NoPhotoText>
            {displayPhotos.length === 0
              ? 'No photos available'
              : 'Select a photo'}
          </NoPhotoText>
        )}
      </PreviewArea>

      {/* Thumbnail strip at bottom */}
      {displayPhotos.length > 0 && (
        <ThumbnailStrip>
          <ThumbnailScrollContainer>
            {displayPhotos.map((photo) => (
              <SmallThumbnail
                key={photo.id}
                photo={photo}
                selected={photo.id === selectedPhoto?.id}
                onClick={() => handleThumbnailClick(photo)}
              />
            ))}
          </ThumbnailScrollContainer>
        </ThumbnailStrip>
      )}
    </PanelContainer>
  )
}
